import { CognitoUserPool } from 'amazon-cognito-identity-js';

// Cognito Configuration
const COGNITO_CONFIG = {
  UserPoolId: 'us-west-2_NYS3aV59r',
  ClientId: '5u5innfog6g34ii2etusfrj0dh',
  Region: 'us-west-2'
};

// Initialize Cognito User Pool
const userPool = new CognitoUserPool({
  UserPoolId: COGNITO_CONFIG.UserPoolId,
  ClientId: COGNITO_CONFIG.ClientId
});

export interface CognitoSession {
  isValid: boolean;
  accessToken: string;
  idToken: string;
  refreshToken?: string;
  userAttributes?: any;
}

/**
 * Check if there's an existing Cognito session (works across subdomains via cookies)
 * This is the key function for cross-subdomain SSO
 */
export async function checkCognitoSession(): Promise<CognitoSession | null> {
  return new Promise((resolve) => {
    try {
      const cognitoUser = userPool.getCurrentUser();
      
      if (!cognitoUser) {
        console.log('[CognitoService] No current user found');
        resolve(null);
        return;
      }

      console.log('[CognitoService] Found current user, checking session...');

      // Get session - this checks cookies that are shared across subdomains
      cognitoUser.getSession((err: Error | null, session: any) => {
        if (err) {
          console.error('[CognitoService] Error getting session:', err);
          resolve(null);
          return;
        }

        if (!session || !session.isValid()) {
          console.log('[CognitoService] Session is invalid or expired');
          resolve(null);
          return;
        }

        console.log('[CognitoService] Valid Cognito session found');

        // Get user attributes
        cognitoUser.getUserAttributes((attrErr, attributes) => {
          if (attrErr) {
            console.error('[CognitoService] Error getting user attributes:', attrErr);
          }

          const userAttributes: any = {};
          if (attributes) {
            attributes.forEach((attr) => {
              userAttributes[attr.Name] = attr.Value;
            });
          }

          resolve({
            isValid: true,
            accessToken: session.getAccessToken().getJwtToken(),
            idToken: session.getIdToken().getJwtToken(),
            refreshToken: session.getRefreshToken()?.getToken(),
            userAttributes
          });
        });
      });
    } catch (error) {
      console.error('[CognitoService] Error checking Cognito session:', error);
      resolve(null);
    }
  });
}

/**
 * Get user attributes from current Cognito session
 */
export async function getCognitoUserAttributes(): Promise<any> {
  return new Promise((resolve, reject) => {
    try {
      const cognitoUser = userPool.getCurrentUser();
      
      if (!cognitoUser) {
        reject(new Error('No current user'));
        return;
      }

      cognitoUser.getUserAttributes((err, attributes) => {
        if (err) {
          reject(err);
          return;
        }

        const userAttributes: any = {};
        if (attributes) {
          attributes.forEach((attr) => {
            userAttributes[attr.Name] = attr.Value;
          });
        }

        resolve(userAttributes);
      });
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Sign out from Cognito (clears cookies shared across subdomains)
 */
export function signOutCognito(): void {
  try {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
      console.log('[CognitoService] User signed out from Cognito');
    }
  } catch (error) {
    console.error('[CognitoService] Error signing out:', error);
  }
}

export { userPool, COGNITO_CONFIG };

